import React from 'react';
import { Package, Star, Rocket } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBreakpoint } from '@/hooks/use-mobile';
import PlanCard from './PlanCard';
import PlanComparisonTable from './PlanComparisonTable';
import { PlanData, FeatureCategory } from './types';

const PricingTiers = () => {
  const { isMobile } = useBreakpoint();
  
  const plans: PlanData[] = [{
    name: 'Starter',
    price: '$29',
    period: '/mo',
    description: 'Perfect for small businesses just getting started with AI.',
    ctaText: 'Select Starter',
    highlight: false,
    icon: <Package className="w-5 h-5" />,
    tagline: 'Get started',
    color: 'bg-gray-100'
  }, {
    name: 'Unlimited',
    price: '$99',
    period: '/mo',
    description: 'For enterprises needing full customization and scalability.',
    ctaText: 'Select Unlimited',
    highlight: true,
    icon: <Rocket className="w-5 h-5" />,
    tagline: 'All features',
    color: 'bg-[#003a70]/5'
  }, {
    name: 'Pro',
    price: '$59',
    period: '/mo',
    description: 'Ideal for growing businesses with multiple AI needs.',
    ctaText: 'Select Pro',
    highlight: false,
    icon: <Star className="w-5 h-5" />,
    tagline: 'Most popular',
    color: 'bg-[#FEE6D8]/50'
  }];
  
  const featureCategories: FeatureCategory[] = [{
    name: 'Core Features',
    features: [{
      name: 'Assistants / Bots / IntelliForms',
      description: 'Number of AI assistants, chatbots, and intelligent forms you can create',
      values: {
        Starter: 3,
        Unlimited: 'Unlimited',
        Pro: 20
      }
    }, {
      name: 'Bring Your Own API Key (BYOK)',
      description: 'Use your own API keys from OpenAI, Anthropic, or Google',
      values: {
        Starter: true,
        Unlimited: true,
        Pro: true
      }
    }, {
      name: 'Drag & Drop Visual Flow Builder',
      description: 'Create AI conversation flows with our easy-to-use visual editor',
      values: {
        Starter: true,
        Unlimited: true,
        Pro: true
      }
    }]
  }, {
    name: 'AI Capabilities',
    features: [{
      name: 'AI Model Flexibility',
      description: 'Support for various AI models including OpenAI, Claude, and Gemini',
      values: {
        Starter: true,
        Unlimited: true,
        Pro: true
      }
    }, {
      name: 'Per Bot API Key',
      description: 'Assign different API keys to different bots',
      values: {
        Starter: true,
        Unlimited: true,
        Pro: true
      }
    }, {
      name: 'Context & Knowledge Sources',
      description: 'Connect various data sources to enhance your AI',
      values: {
        Starter: 'Files, Web, Q&A, RSS, Google Sheets, Google Docs',
        Unlimited: 'Same',
        Pro: 'Same'
      }
    }]
  }, {
    name: 'Customization & Branding',
    features: [{
      name: 'White Label Bots',
      description: 'Remove AskSpot branding from your AI assistants',
      values: {
        Starter: true,
        Unlimited: true,
        Pro: true
      }
    }, {
      name: 'Custom Domain',
      description: 'Use your own domain for your AI assistants',
      values: {
        Starter: false,
        Unlimited: true,
        Pro: true
      }
    }]
  }, {
    name: 'Analytics & Management',
    features: [{
      name: 'Chat Logs & Export',
      description: 'Access and export conversation history',
      values: {
        Starter: true,
        Unlimited: true,
        Pro: true
      }
    }, {
      name: 'Chat Limiting',
      description: 'Set limits to prevent abuse and control costs',
      values: {
        Starter: true,
        Unlimited: true,
        Pro: true
      }
    }, {
      name: 'Lead Capture Engine',
      description: 'Collect user information through your AI assistants',
      values: {
        Starter: true,
        Unlimited: true,
        Pro: true
      }
    }, {
      name: 'Prompt Library',
      description: 'Access pre-built prompts and templates',
      values: {
        Starter: true,
        Unlimited: true,
        Pro: true
      }
    }, {
      name: 'Visibility Controls',
      description: 'Control who can access your AI assistants',
      values: {
        Starter: true,
        Unlimited: true,
        Pro: true
      }
    }, {
      name: 'Webhooks',
      description: 'Connect your AI assistants to other services',
      values: {
        Starter: true,
        Unlimited: true,
        Pro: true
      }
    }]
  }];
  
  return (
    <div className="max-w-7xl mx-auto px-4" data-pricing-table>
      <div className="text-center mb-6 md:mb-10">
        <h2 className="text-2xl md:text-3xl font-bold text-[#003a70] mb-2 md:mb-3">
          Simple, Transparent Pricing
        </h2>
        <p className="text-base md:text-lg text-gray-600 max-w-xl mx-auto">
          Choose the perfect plan for your business needs
        </p>
        <p className="text-base md:text-lg font-bold text-[#003a70] mt-3 bg-[#FEE6D8]/50 inline-block px-4 py-2 rounded-md mx-auto">
          Start your 30-Day Risk-Free Trial today! Cancel anytime directly from your dashboard.
        </p>
      </div>
      
      {isMobile ? (
        <div className="space-y-8">
          {plans.map(plan => <PlanCard key={plan.name} plan={plan} featureCategories={featureCategories} />)}
          
          <div className="mt-8 text-center text-sm text-gray-500 bg-[#FEE6D8]/30 p-4 rounded-lg">
            ✨ No credit card required to start. All plans include 14-day free trial.
          </div>
        </div>
      ) : (
        <div>
          <PlanComparisonTable plans={plans} featureCategories={featureCategories} />
        </div>
      )}
    </div>
  );
};

export default PricingTiers;
